import i18n from 'i18next';
import {showNotification} from 'platform/components';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import {useLatestValue, useOnMount} from 'shared/web';

import type {BulkOperationResponseBody} from '../types/api/bulkOperations';
import type {BulkOperationsServices} from '../types/BulkOperationsServices';
import {createPendingBulkOperation} from '../utils/createPendingBulkOperation';
import {normalizeBulkOperationError} from '../utils/normalizeBulkOperationError';

const handleApiError = () =>
  showNotification.error(i18n.t('general.notifications.defaultErrorMessage'));

/** Owns reruns for the tracking lifetime, independently of panel and dialog visibility. */
export function useBulkOperationRecovery(args: {
  services: Pick<BulkOperationsServices, 'useRerunFailed' | 'handleApiError'>;
  onRegistered: (operation: BulkOperationResponseBody, sourceOperationId: string) => void;
}) {
  const [submit] = args.services.useRerunFailed();
  const latest = useLatestValue({...args, submit});
  const owner = useRef({active: true, generation: 0, pending: new Set<string>()});
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  useOnMount(
    () => {
      owner.current.active = true;
    },
    () => {
      owner.current.active = false;
      owner.current.generation += 1;
      owner.current.pending.clear();
    }
  );

  const rerun = async (operation: BulkOperationResponseBody) => {
    const lifecycle = owner.current;
    const sourceOperationId = operation.bulkOperationId;
    if (!lifecycle.active || lifecycle.pending.has(sourceOperationId)) {
      return;
    }
    const generation = lifecycle.generation;
    lifecycle.pending.add(sourceOperationId);
    setPendingIds(new Set(lifecycle.pending));
    await latest.current
      .submit({bulkOperationId: sourceOperationId})
      .unwrap()
      .then((response) => {
        if (!isNil(response) && lifecycle.active && lifecycle.generation === generation) {
          latest.current.onRegistered(
            createPendingBulkOperation({
              actionCode: operation.actionCode,
              bulkOperationId: response.bulkOperationId,
              resourceId: operation.resourceId,
              totalCount: operation.failedCount,
            }),
            sourceOperationId
          );
        }
      })
      .catch((error) => {
        if (lifecycle.active && lifecycle.generation === generation) {
          (latest.current.services.handleApiError ?? handleApiError)(
            normalizeBulkOperationError(error)
          );
        }
      })
      .finally(() => {
        if (lifecycle.active && lifecycle.generation === generation) {
          lifecycle.pending.delete(sourceOperationId);
          setPendingIds(new Set(lifecycle.pending));
        }
      });
  };

  return {pendingIds, rerun};
}
